import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../composants/Button/Button';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import TableauComplements from '../../../composants/TableauComplements/TableauComplements';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import { OperationBC, OperationLienBC } from '../types';
import { creerComplement } from '../services/operationBcService';
import { estPrincipale, lignesComplements } from '../domain/complements';
import './CommandesComplementaires.css';

interface CommandesComplementairesProps {
  operation: OperationBC;
}

// Section « Commandes complementaires » de l'onglet Notification et signature, cote ACHETEUR. Depuis
// un bon accepte, il cree un bon complementaire pour le meme titulaire : nouvelle operation en
// brouillon, meme descriptif, panier vide, nom suffixe « (complement N°X) ». Le tableau liste la
// commande initiale puis ses complements ; sur un complement, meme tableau sans bouton : pas de
// complement de complement. Tous les bons lies sont les siens : chaque ligne s'ouvre.
const CommandesComplementaires: React.FC<CommandesComplementairesProps> = ({ operation }) => {
  const navigate = useNavigate();
  const [confirmationOuverte, setConfirmationOuverte] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const creer = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await creerComplement(operation.id);
      if (reponse?.data?.id) {
        // Sans `?tab=` : pour un brouillon, l'onglet par defaut est deja la Consultation du seul rang,
        // la ou l'acheteur compose le panier ou transfere au titulaire.
        navigate(`/operation-bc/${reponse.data.id}`);
      } else {
        setErreur("La commande complémentaire a été créée mais son identifiant n'a pas été renvoyé. Rechargez la page.");
      }
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Impossible de créer la commande complémentaire.');
    } finally {
      // La popup se ferme dans tous les cas : le message d'erreur est rendu dans la section, il
      // serait invisible derriere l'overlay de la modale.
      setConfirmationOuverte(false);
      setEnCours(false);
    }
  };

  return (
    <SectionPliable title="Commandes complémentaires" defaultOpen>
      <div className="section-content">
        {estPrincipale(operation) && (
          <div className="no-print bc-complements__actions">
            <Button
              value="Créer une commande complémentaire"
              variant="primary"
              onClick={() => setConfirmationOuverte(true)}
              disabled={enCours}
            />
          </div>
        )}
        {erreur && <div className="cmd-offre__inline-message cmd-offre__inline-message--error">{erreur}</div>}
        <TableauComplements<OperationLienBC>
          lignes={lignesComplements(operation)}
          idCourant={operation.id}
          estOuvrable={() => true}
          onOuvrir={(ligne) => navigate(`/operation-bc/${ligne.id}`)}
        />
      </div>

      <ValidationPopUp
        isOpen={confirmationOuverte}
        title="Créer une commande complémentaire"
        text="Une nouvelle commande sera créée en brouillon pour le même titulaire, avec le descriptif de ce bon de commande et un panier vide. Vous pourrez ensuite saisir les prestations ou laisser le titulaire les chiffrer."
        confirmLabel="Créer"
        cancelLabel="Annuler"
        onClose={() => setConfirmationOuverte(false)}
        onConfirm={creer}
        isLoading={enCours}
      />
    </SectionPliable>
  );
};

export default CommandesComplementaires;
